import { ListEntity } from "models/list-entity";
import { Locale } from "models/locale";
import { parse } from "./locale";
import { ColumnInterface } from "components/tables/interfaces";
import { ListUser } from "models/list-user";
import { SchemaColumn } from "models/generic";
import { parseUserValues } from "./collection-values";
import { ListEntityField } from "models/list-entity-field";
import { TranslateFn } from "models/translation";
import { Fragment } from "react/jsx-runtime";
import Indicator from "components/indicator";
import { timestamp } from "./date";
import { findFieldByIdentifier } from "./field";
import { EntityCollection } from "models/list-user-entity-collection";

// this is used in settings views
export const renderListSchemaColumn = (
  schema: SchemaColumn,
  entities: ListEntity[],
  locale: Locale,
  translate: TranslateFn,
) => {
  const { type, fieldUuid } = schema;
  if (type === "field" && fieldUuid) {
    const match = getListEntityAndField(fieldUuid, entities);
    if (match) {
      const [entity, field] = match;
      return [parse(entity.name, locale), parse(field.name, locale)].join(", ");
    }
  } else {
    return translate(`column_type_${type}`);
  }
};

// get a single field by uuid, finds nested ones
export const getListEntityAndField = (
  fieldUuid: string,
  listEntities: ListEntity[],
): [ListEntity, ListEntityField] | null => {
  for (let e = 0; e < listEntities.length; e += 1) {
    const { listEntityFields = [] } = listEntities[e];
    for (let f = 0; f < listEntityFields.length; f += 1) {
      const field = listEntityFields[f];
      if (field.uuid === fieldUuid) {
        return [listEntities[e], field];
      }
    }
  }

  return null;
};

export const addUserColumns = (
  schema: SchemaColumn[],
  locale: Locale,
  listEntities: ListEntity[],
  columnsRef: ColumnInterface[],
  translate: TranslateFn,
) => {
  schema.forEach((schema) => {
    const { type } = schema;
    if (type === "field") {
      addUserFieldColumn(schema, locale, listEntities, columnsRef);
    } else {
      addUserSpecialColumn(schema, locale, listEntities, columnsRef, translate);
    }
  });
};

export const addEntityCollectionColumns = (
  schema: SchemaColumn[],
  locale: Locale,
  listEntities: ListEntity[],
  columnsRef: ColumnInterface[],
  translate: TranslateFn,
) => {
  schema.forEach((schema) => {
    const { type } = schema;
    if (type === "field") {
      addEntityCollectionFieldColumn(schema, locale, listEntities, columnsRef);
    } else {
      addEntityCollectionSpecialColumn(
        schema,
        listEntities,
        columnsRef,
        translate,
      );
    }
  });
};

const addUserFieldColumn = (
  schema: SchemaColumn,
  locale: Locale,
  listEntities: ListEntity[],
  columnsRef: ColumnInterface[],
) => {
  const { fieldUuid, width } = schema;
  if (!fieldUuid) {
    return;
  }

  const match = getListEntityAndField(fieldUuid, listEntities);
  if (match) {
    const [_entity, field] = match;
    columnsRef.push({
      key: `field-${field.uuid}`,
      header: parse(field.name, locale),
      width,
      render: (user: ListUser) => {
        const { entityCollections = [] } = user;
        return (
          parseUserValues(field, entityCollections, locale).join(", ") || "-"
        );
      },
    });
  } else {
    // add nothing
  }
};

const addEntityCollectionFieldColumn = (
  schema: SchemaColumn,
  locale: Locale,
  listEntities: ListEntity[],
  columnsRef: ColumnInterface[],
) => {
  const { fieldUuid, width } = schema;
  if (!fieldUuid) {
    return;
  }

  const match = getListEntityAndField(fieldUuid, listEntities);
  if (match) {
    const [_entity, field] = match;
    columnsRef.push({
      key: `field-${field.uuid}`,
      header: parse(field.name, locale),
      width,
      render: (entityCollection: EntityCollection) => {
        const joined = parseUserValues(field, [entityCollection], locale).join(
          ", ",
        );
        return joined || "-";
      },
    });
  } else {
    // add nothing
  }
};

const addUserSpecialColumn = (
  schema: SchemaColumn,
  locale: Locale,
  listEntities: ListEntity[],
  columnsRef: ColumnInterface[],
  translate: TranslateFn,
) => {
  const { type } = schema;
  switch (type) {
    case "name":
      columnsRef.push({
        key: "special-name",
        header: translate("column_type_name"),
        render: (user: ListUser) => {
          // use name, if available
          const name = [user.lastname, user.firstname].join(" ").trim();
          if (name.length > 0) {
            return name;
          }

          // fallback 1: email
          if (user.email) {
            return user.email;
          }

          // fallback 2: dash
          return "-";
        },
      });
      break;
    case "liabilities":
      columnsRef.push({
        key: "special-liabilities",
        header: translate("column_type_liabilities"),
        render: (user: ListUser) => {
          // find fields
          const unitField = findFieldByIdentifier(listEntities, "UNIT");
          const positionField = findFieldByIdentifier(listEntities, "POSITION");

          // find matching values
          if (unitField && positionField) {
            const unitValues = parseUserValues(
              unitField,
              user.entityCollections || [],
              locale,
            );
            const positionValues = parseUserValues(
              positionField,
              user.entityCollections || [],
              locale,
            );
            return (
              <Fragment>
                {positionValues.map((positionValue, index) => {
                  const unitValue = unitValues[index];
                  return (
                    <div key={index}>
                      {positionValue} - {unitValue}
                    </div>
                  );
                })}
              </Fragment>
            );
          }
          return "";
        },
      });
      break;
    case "modified":
      columnsRef.push({
        key: `special-updated`,
        header: translate("column_type_modified"),
        render: (user: ListUser) => {
          return timestamp(user.updated_at);
        },
      });
      break;
    case "status":
      columnsRef.push({
        key: `special-status`,
        header: translate("column_type_status"),
        render: (user: ListUser) => {
          const {
            status,
            updated_at,
            invited_at,
            draft_at,
            submitted_at,
            approved_at,
          } = user;
          const renderStatus = () => {
            if (status === "invited") {
              return timestamp(invited_at || updated_at);
            }
            if (status === "draft") {
              return timestamp(draft_at || updated_at);
            }
            if (status === "submitted") {
              return timestamp(submitted_at || updated_at);
            }
            if (status === "approved") {
              return timestamp(approved_at || updated_at);
            }
            return "";
          };
          return (
            <Fragment>
              <Indicator type={status === "approved" ? "positive" : "negative"}>
                {translate(`user_status_${status}`)}
              </Indicator>
              {renderStatus()}
            </Fragment>
          );
        },
      });
      break;
    default:
    // add nothing
  }
};

const addEntityCollectionSpecialColumn = (
  _schema: SchemaColumn,
  _listEntities: ListEntity[],
  _columnsRef: ColumnInterface[],
  _translate: TranslateFn,
) => {
  // add nothing
};
